import { inkStroke, peak, scroll, splats, sword } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ขีดเดียวไร้ถ้อย (หอหมึกสน, soft): one stroke, no words. A plain dark-hilted jian, a single ink line
 * that opens late (a pale split along the stroke, no blood), paper, ink mountains and a pine screen.
 */

const INK = '#1e1a24';
const PAPER = C.paper;
const PAPER_SH = '#d8d0c0';
const WASH = '#8ab8d0';
const PINE = '#4a9a6a';
const HILT = '#3a4a5a';
const TASSEL = '#5cb8d8';

const plainSword = (p: Pix, x0: number, y0: number, x1: number, y1: number) =>
  sword(p, x0, y0, x1, y1, { hilt: HILT, tassel: TASSEL });

/** A sheet of paper with a shaded lower edge. */
function sheet(p: Pix, x: number, y: number, w: number, h: number): void {
  p.rect(x, y, w, h, PAPER);
  p.rect(x, y + h - 1, w, 1, PAPER_SH);
}

/** The late-opening line: ink with a pale split down its middle. */
function lateLine(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  p.thick(x0, y0, x1, y1, INK, 3);
  p.line(x0, y0, x1, y1, WASH);
}

registerIcons('skill', {
  // One exact line across the tile; the sword already past it.
  'sg-line': (p) => {
    lateLine(p, 3, 27, 22, 8);
    plainSword(p, 18, 30, 29, 19);
    p.px(25, 6, PAPER).px(27, 4, PAPER);
  },
  // A verse written in the air breaks the guard: lines of script cut through.
  'sg-verse': (p) => {
    sheet(p, 4, 3, 16, 17);
    for (const x of [7, 11, 15]) inkStroke(p, [
      [x, 6],
      [x + 1, 11],
      [x, 16],
    ], 2, INK);
    lateLine(p, 2, 22, 22, 2);
    plainSword(p, 4, 29, 13, 20);
  },
  // Breath held, then one stroke: two marks wait on the line.
  'sg-rhyme': (p) => {
    p.thick(4, 16, 28, 16, INK, 3);
    p.line(4, 16, 28, 16, WASH);
    for (const x of [11, 20]) p.rect(x, 11, 2, 3, PAPER).rect(x, 19, 2, 3, PAPER);
    plainSword(p, 4, 28, 28, 28);
    p.px(16, 6, WASH).px(15, 7, WASH).px(17, 7, WASH);
  },
  // A dash like a dragged brush: a long tapering ink trail, the sword at its head.
  'sg-stroke': (p) => {
    inkStroke(p, [
      [2, 24],
      [10, 20],
      [18, 17],
    ], 4, INK);
    p.line(4, 23, 17, 17, WASH);
    plainSword(p, 13, 19, 30, 10);
    splats(p, [
      [5, 28, 1],
      [9, 27, 0.7],
    ], INK);
  },
  // A landscape scroll unrolled on the ground; whoever stands in it becomes a ridge line.
  'sg-scroll': (p) => {
    scroll(p, 5, 9, 22, 18, PAPER, '#7a4a2a');
    peak(p, 12, 23, 12, 10, INK, null);
    peak(p, 20, 23, 12, 7, WASH, null);
    p.rect(6, 23, 20, 1, INK);
    p.ell(22, 13, 1.5, 1.5, '#d8452a');
  },
  // A mind blank as paper: one dot of ink, the sword upright and still.
  'sg-insight': (p) => {
    sheet(p, 9, 6, 18, 22);
    p.ell(18, 15, 2, 2, INK);
    plainSword(p, 5, 30, 5, 4);
  },
  // A painted screen drawn in qi: three panels, a pine on them.
  'sg-screen': (p) => {
    for (const x of [4, 13, 22]) {
      sheet(p, x, 6, 8, 20);
      p.rect(x, 5, 8, 1, '#7a4a2a').rect(x, 26, 8, 1, '#7a4a2a');
    }
    p.thick(9, 24, 20, 10, INK, 2);
    for (const [x, y] of [
      [11, 18],
      [16, 13],
      [20, 10],
    ] as const)
      p.rect(x - 3, y, 7, 2, PINE);
  },
  // The masterpiece: ink bursts twice round a mountain painted in one sweep.
  'sg-masterpiece': (p) => {
    p.ring(16, 18, 12, 9, INK, 0, Math.PI * 2);
    p.ring(16, 18, 8, 6, WASH, 0, Math.PI * 2);
    peak(p, 16, 22, 14, 13, INK, PAPER);
    splats(p, [
      [5, 6, 1.5],
      [26, 7, 1.2],
      [27, 25, 1.5],
      [6, 26, 1],
    ], INK);
    lateLine(p, 5, 24, 27, 22);
  },
});
